import { useTheme } from "@mui/material/styles";
import { Paper } from "@mui/material";

import { PERM_UPPER_RIGHT, PERM_UPPER_LEFT, PERM_LOWER_RIGHT, PERM_LOWER_LEFT, PERMANENT_TO_DECIDUOUS } from "../../../../config/constants/Constants";

/**
 * PeriodontalChart Component
 * Renders a visual periodontal chart similar to periodontalchart-online.com
 * Displays upper or lower arch with buccal and palatal/lingual views
 *
 * @param {Object} props.teethData - The examination teeth data
 * @param {string} props.arch - 'upper' or 'lower'
 * @param {Object} props.patientInfo - Patient information for header
 */
export default function PeriodontalChart({ teethData, arch = "upper", patientInfo = {} }) {
    const theme = useTheme();
    const isDark = theme.palette.mode === "dark";

    if (!teethData) return null;

    // Determine teeth numbers based on arch
    const rightTeeth = arch === "upper" ? PERM_UPPER_RIGHT : PERM_LOWER_RIGHT;
    const leftTeeth = arch === "upper" ? PERM_UPPER_LEFT : PERM_LOWER_LEFT;
    const allTeeth = [...rightTeeth, ...leftTeeth];

    // View labels
    const topView = arch === "upper" ? "Buccal" : "Buccal";
    const bottomView = arch === "upper" ? "Palatal" : "Lingual";

    // Helper to get effective tooth for a permanent position
    // Returns deciduous tooth number if it exists and permanent is missing
    const getEffectiveTooth = (permanentToothNum) => {
        const deciduousTooth = PERMANENT_TO_DECIDUOUS[permanentToothNum];

        if (deciduousTooth && teethData[deciduousTooth] && !teethData[deciduousTooth].missing && teethData[permanentToothNum]?.missing) {
            return deciduousTooth;
        }

        return permanentToothNum;
    };

    // Chart configuration
    const headerHeight = 70;

    const gridHeight = 100; // 16mm grid
    const gridMm = 16;
    const pxPerMm = gridHeight / gridMm; // 6.25px per mm
    const toothWidth = 50;
    const toothHeight = 60; // Crown + partial root

    const midlineGap = 20;
    const tableGridGap = 10; // Gap between table and grid

    const chartWidth = 960;
    const chartHeight = 660; // Fixed height without statistics section

    const tableRowHeight = 15;
    const tableLabels = ["Tooth #", "Mobility", "Implant", "Furcation", "Bleeding", "Plaque", "Suppuration", "Recession", "Probing Depth"];
    const tableHeight = tableRowHeight * tableLabels.length;
    const tableLabelOffset = -5;
    const tableScaleOffset = -6;

    const upperGridLegendX = -20;
    const upperGridLegendY = 220;

    const lowerGridLegendX = -20;
    const lowerGridLegendY = 135;

    const midlineY2 = 260;

    const upperTableX = 70;
    const upperTableY = 90;

    const lowerTableX = 70;
    const lowerTableY = 345;

    /**
     * Get tooth type based on tooth number (FDI notation)
     */
    const getToothType = (toothNum) => {
        const lastDigit = toothNum % 10;
        if (lastDigit === 1 || lastDigit === 2) return "incisor";
        if (lastDigit === 3) return "canine";
        if (lastDigit === 4 || lastDigit === 5) return "premolar";
        if (lastDigit === 6 || lastDigit === 7 || lastDigit === 8) return "molar";
        return "incisor";
    };

    /**
     * Render anatomically accurate tooth crown SVG
     * Different crown morphology for each tooth type based on dental anatomy
     * Crown height ~18px to not overlap with root SVG
     */
    const renderToothCrown = (toothNum, width, height, isMissing, isImplant, isBuccal = false) => {
        const crownH = 24;
        const buccalTranslate = -(tableHeight + tableGridGap + gridHeight + 2 + crownH);
        const palatalTranslate = toothHeight - crownH - 2;
        const crownTransform = isBuccal ? `scale(1, -1) translate(0, ${buccalTranslate})` : `scale(1, 1) translate(0, ${palatalTranslate})`;

        if (isMissing) {
            return (
                <g transform={crownTransform}>
                    <line x1={5} y1={40} x2={width - 5} y2={crownH + 90} stroke="#999" strokeWidth={2} />
                    <line x1={width - 5} y1={40} x2={5} y2={crownH + 90} stroke="#999" strokeWidth={2} />
                </g>
            );
        }

        const type = getToothType(toothNum);
        const cx = width / 2;
        const lastDigit = toothNum % 10;

        // Crown colors
        const crownFill = isImplant ? (isDark ? "#4eacf8" : "#e3f2fd") : isDark ? "#787878" : "#f8f8f8";
        const crownStroke = isDark ? "#686868" : "#c0c0c0";
        const anatomyStroke = isDark ? "#888" : "#aaa";

        let crownElements = [];

        // Rhombus crown shape: narrow occlusal → widens to contact at ~1/3 → tapers to narrower cervical
        if (type === "incisor") {
            const isLateral = lastDigit === 2;
            const tW = isLateral ? 9 : 11; // top (incisal) half-width - narrow for rhombus
            const mW = isLateral ? 18 : 21; // max contact half-width
            const cW = isLateral ? 10 : 12; // cervical half-width

            const crownPath = `
                M ${cx - tW} 0
                L ${cx + tW} 0
                Q ${cx + tW + 4} ${crownH * 0.08}, ${cx + mW} ${crownH * 0.3}
                Q ${cx + mW} ${crownH * 0.7}, ${cx + cW} ${crownH}
                L ${cx - cW} ${crownH}
                Q ${cx - mW} ${crownH * 0.7}, ${cx - mW} ${crownH * 0.3}
                Q ${cx - tW - 4} ${crownH * 0.08}, ${cx - tW} 0
                Z
            `;

            crownElements.push(<path key="crown" d={crownPath} fill={crownFill} stroke={crownStroke} strokeWidth={1.2} />);
            crownElements.push(<line key="incisal" x1={cx - tW + 1} y1={1} x2={cx + tW - 1} y2={1} stroke={anatomyStroke} strokeWidth={0.6} />);
        } else if (type === "canine") {
            const mW = 23; // max contact half-width
            const cW = 14; // cervical half-width

            const crownPath = `
                M ${cx} 0
                Q ${cx - 6} ${crownH * 0.12}, ${cx - mW} ${crownH * 0.38}
                Q ${cx - mW} ${crownH * 0.72}, ${cx - cW} ${crownH}
                L ${cx + cW} ${crownH}
                Q ${cx + mW} ${crownH * 0.72}, ${cx + mW} ${crownH * 0.38}
                Q ${cx + 6} ${crownH * 0.12}, ${cx} 0
                Z
            `;

            crownElements.push(<path key="crown" d={crownPath} fill={crownFill} stroke={crownStroke} strokeWidth={1.2} />);
            crownElements.push(<path key="ridge-m" d={`M ${cx} 1 Q ${cx - 5} ${crownH * 0.15}, ${cx - mW + 4} ${crownH * 0.35}`} fill="none" stroke={anatomyStroke} strokeWidth={0.5} />);
            crownElements.push(<path key="ridge-d" d={`M ${cx} 1 Q ${cx + 5} ${crownH * 0.15}, ${cx + mW - 4} ${crownH * 0.35}`} fill="none" stroke={anatomyStroke} strokeWidth={0.5} />);
        } else if (type === "premolar") {
            const mW = 22; // max contact half-width
            const cW = 15; // cervical half-width
            const cuspW = 6;

            const crownPath = `
                M ${cx - cuspW} 2
                Q ${cx} 0, ${cx + cuspW} 2
                Q ${cx + mW * 0.7} ${crownH * 0.1}, ${cx + mW} ${crownH * 0.33}
                Q ${cx + mW} ${crownH * 0.72}, ${cx + cW} ${crownH}
                L ${cx - cW} ${crownH}
                Q ${cx - mW} ${crownH * 0.72}, ${cx - mW} ${crownH * 0.33}
                Q ${cx - mW * 0.7} ${crownH * 0.1}, ${cx - cuspW} 2
                Z
            `;

            crownElements.push(<path key="crown" d={crownPath} fill={crownFill} stroke={crownStroke} strokeWidth={1.2} />);
            crownElements.push(<path key="cusp" d={`M ${cx - cuspW - 1} 3 Q ${cx} 0, ${cx + cuspW + 1} 3`} fill="none" stroke={anatomyStroke} strokeWidth={0.5} />);
        } else {
            // Molar: two buccal cusps with rhombus body
            const is3rdMolar = lastDigit === 8;
            const mW = is3rdMolar ? 22 : 24; // max contact half-width
            const cW = is3rdMolar ? 15 : 17; // cervical half-width
            const cusp1X = cx - mW * 0.3;
            const cusp2X = cx + mW * 0.3;

            const crownPath = `
                M ${cusp1X - 4} 3
                Q ${cusp1X} 0, ${cusp1X + 2} 2
                Q ${cx} 5, ${cusp2X - 2} 2
                Q ${cusp2X} 0, ${cusp2X + 4} 3
                Q ${cx + mW * 0.7} ${crownH * 0.1}, ${cx + mW} ${crownH * 0.3}
                Q ${cx + mW} ${crownH * 0.72}, ${cx + cW} ${crownH}
                L ${cx - cW} ${crownH}
                Q ${cx - mW} ${crownH * 0.72}, ${cx - mW} ${crownH * 0.3}
                Q ${cx - mW * 0.7} ${crownH * 0.1}, ${cusp1X - 4} 3
                Z
            `;

            crownElements.push(<path key="crown" d={crownPath} fill={crownFill} stroke={crownStroke} strokeWidth={1.2} />);
            crownElements.push(<line key="groove" x1={cx} y1={5} x2={cx} y2={crownH * 0.55} stroke={anatomyStroke} strokeWidth={0.6} />);
            crownElements.push(<path key="cusp1" d={`M ${cusp1X - 4} 4 Q ${cusp1X} 1, ${cusp1X + 4} 4`} fill="none" stroke={anatomyStroke} strokeWidth={0.5} />);
            crownElements.push(<path key="cusp2" d={`M ${cusp2X - 4} 4 Q ${cusp2X} 1, ${cusp2X + 4} 4`} fill="none" stroke={anatomyStroke} strokeWidth={0.5} />);
        }

        return <g transform={crownTransform}>{crownElements}</g>;
    };

    /**
     * Render dental implant screw SVG with smooth rounded end
     */
    const renderImplantScrew = (centerX, height) => {
        const screwWidth = 18;
        const screwHeight = 75;
        const threadCount = 8;
        const threadDepth = 2;
        const halfWidth = screwWidth / 2;

        // Colors
        const implantFill = isDark ? "#a0a0a0" : "#e7e7e7";
        const implantStroke = isDark ? "#666" : "#888";
        const threadStroke = isDark ? "#666" : "#999";
        const collarFill = isDark ? "#b0b0b0" : "#d0d0d0";
        const collarStroke = isDark ? "#666" : "#888";

        // Create screw body path with smooth rounded end (not pointy)
        const screwBody = `
            M ${centerX - halfWidth} 0
            L ${centerX - halfWidth} ${screwHeight - 8}
            Q ${centerX - halfWidth} ${screwHeight} ${centerX} ${screwHeight}
            Q ${centerX + halfWidth} ${screwHeight} ${centerX + halfWidth} ${screwHeight - 8}
            L ${centerX + halfWidth} 0
            Z
        `;

        // Create thread grooves
        const threads = [];
        const threadSpacing = (screwHeight - 10) / threadCount;
        for (let i = 1; i < threadCount; i++) {
            const y = i * threadSpacing;
            threads.push(
                <path
                    key={`thread-${i}`}
                    d={`M ${centerX - halfWidth - threadDepth} ${y}
                        L ${centerX - halfWidth} ${y + 3}
                        M ${centerX + halfWidth} ${y + 3}
                        L ${centerX + halfWidth + threadDepth} ${y}`}
                    stroke={threadStroke}
                    strokeWidth={1.5}
                    fill="none"
                />
            );
        }

        // Abutment collar at top
        const collarHeight = 8;
        const collar = `
            M ${centerX - halfWidth - 2} -${collarHeight}
            L ${centerX - halfWidth - 2} 0
            L ${centerX + halfWidth + 2} 0
            L ${centerX + halfWidth + 2} -${collarHeight}
            Z
        `;

        return (
            <g>
                {/* Implant body */}
                <path d={screwBody} fill={implantFill} stroke={implantStroke} strokeWidth={1} />
                {/* Thread grooves */}
                {threads}
                {/* Abutment collar */}
                <path d={collar} fill={collarFill} stroke={collarStroke} strokeWidth={1} />
            </g>
        );
    };

    /**
     * Render anatomically accurate tooth roots SVG
     * Different root morphology for each tooth type based on dental anatomy
     * Roots have natural curves, common trunk splitting halfway, with overlapping roots for molars
     */
    const renderToothRoots = (toothNum, width, height, isMissing, isImplant, isBuccal = false) => {
        if (isMissing) return null;

        const type = getToothType(toothNum);
        const centerX = width / 2;
        const isUpper = Math.floor(toothNum / 10) <= 2 || (Math.floor(toothNum / 10) >= 5 && Math.floor(toothNum / 10) <= 6);

        const transform = isBuccal ? `scale(1, -1) translate(0, ${-height})` : `scale(1, 1) translate(0, ${0})`;

        // If implant, render screw instead of natural root
        if (isImplant) {
            return (
                <g transform={transform} opacity={0.7}>
                    {renderImplantScrew(centerX, height)}
                </g>
            );
        }

        // Light gray for front roots, darker gray for shadowed/hidden roots
        const rootFill = isDark ? "#787878" : "#f8f8f8";
        const rootStroke = isDark ? "#686868" : "#cacaca";
        const rootFillShadow = isDark ? "#696969" : "#eeeeee";
        const rootStrokeShadow = isDark ? "#555555" : "#cacaca";

        let rootElements = [];

        if (type === "incisor") {
            // Single root with pronounced natural curve, narrowing to tip
            const w = 10;
            const h = 75;
            // Curve slightly to the distal (right for Q1/Q4, left for Q2/Q3)
            const curveDir = toothNum % 10 <= 3 ? 3 : -3;
            rootElements.push(
                <path
                    key="root"
                    d={`M ${centerX - w} 0
                        C ${centerX - w + curveDir * 0.3} ${h * 0.25}, ${centerX - w * 0.6 + curveDir} ${h * 0.55}, ${centerX - 1 + curveDir * 0.5} ${h * 0.88}
                        Q ${centerX + curveDir * 0.3} ${h} ${centerX + 1 + curveDir * 0.5} ${h * 0.88}
                        C ${centerX + w * 0.6 + curveDir} ${h * 0.55}, ${centerX + w + curveDir * 0.3} ${h * 0.25}, ${centerX + w} 0
                        Z`}
                    fill={rootFill}
                    stroke={rootStroke}
                    strokeWidth={1}
                />
            );
        } else if (type === "canine") {
            // Long single root with pronounced natural curve
            const w = 11;
            const h = 94;
            // Canines often curve distally
            const curveDir = toothNum % 10 === 3 ? 3 : -3;
            rootElements.push(
                <path
                    key="root"
                    d={`M ${centerX - w} 0
                        C ${centerX - w + curveDir * 0.4} ${h * 0.2}, ${centerX - w * 0.5 + curveDir * 1.2} ${h * 0.5}, ${centerX - 1 + curveDir * 0.8} ${h * 0.9}
                        Q ${centerX + curveDir * 0.5} ${h} ${centerX + 1 + curveDir * 0.8} ${h * 0.9}
                        C ${centerX + w * 0.5 + curveDir * 1.2} ${h * 0.5}, ${centerX + w + curveDir * 0.4} ${h * 0.2}, ${centerX + w} 0
                        Z`}
                    fill={rootFill}
                    stroke={rootStroke}
                    strokeWidth={1}
                />
            );
        } else if (type === "premolar") {
            if (isUpper) {
                // Upper premolars: smooth trunk transitioning to 2 overlapping roots
                const trunkW = 14;
                const splitY = 45;
                const tipH = 78;

                // Palatal root (behind/shadow, curves toward center)
                rootElements.push(
                    <path
                        key="root-palatal"
                        d={`M ${centerX + trunkW * 0.25} ${splitY * 0.6}
                            C ${centerX + trunkW * 0.5} ${splitY}, ${centerX + trunkW * 0.35} ${tipH * 0.5}, ${centerX + 2} ${tipH * 0.85}
                            Q ${centerX + 1} ${tipH} ${centerX} ${tipH * 0.85}
                            C ${centerX - trunkW * 0.1} ${tipH * 0.5}, ${centerX - trunkW * 0.15} ${splitY}, ${centerX - trunkW * 0.1} ${splitY * 0.6}
                            Z`}
                        fill={rootFillShadow}
                        stroke={rootStrokeShadow}
                        strokeWidth={1}
                    />
                );

                // Trunk + buccal root (front, smooth transition, curves outward)
                rootElements.push(
                    <path
                        key="root-buccal"
                        d={`M ${centerX - trunkW} 0
                            C ${centerX - trunkW} ${splitY * 0.5}, ${centerX - trunkW - 0.5} ${splitY}, ${centerX - trunkW - 1} ${splitY + 8}
                            C ${centerX - trunkW - 1.5} ${tipH * 0.6}, ${centerX - trunkW * 0.5} ${tipH * 0.8}, ${centerX - 2} ${tipH * 0.92}
                            Q ${centerX - 1} ${tipH} ${centerX} ${tipH * 0.92}
                            C ${centerX + 1} ${tipH * 0.7}, ${centerX + trunkW * 0.1} ${splitY + 5}, ${centerX + trunkW * 0.1} ${splitY * 0.5}
                            L ${centerX + trunkW * 0.1} 0
                            Z
                            M ${centerX + trunkW * 0.1} 0
                            L ${centerX + trunkW} 0
                            C ${centerX + trunkW} ${splitY * 0.3}, ${centerX + trunkW * 0.5} ${splitY * 0.5}, ${centerX + trunkW * 0.2} ${splitY * 0.6}
                            L ${centerX + trunkW * 0.1} ${splitY * 0.5}
                            Z`}
                        fill={rootFill}
                        stroke={rootStroke}
                        strokeWidth={1}
                    />
                );
            } else {
                // Lower premolars: single curved root with more pronounced curve
                const w = 10;
                const h = 78;
                const curveDir = 3;
                rootElements.push(
                    <path
                        key="root"
                        d={`M ${centerX - w} 0
                            C ${centerX - w + curveDir * 0.3} ${h * 0.25}, ${centerX - w * 0.6 + curveDir} ${h * 0.55}, ${centerX - 1 + curveDir * 0.5} ${h * 0.9}
                            Q ${centerX + curveDir * 0.3} ${h} ${centerX + 1 + curveDir * 0.5} ${h * 0.9}
                            C ${centerX + w * 0.6 + curveDir} ${h * 0.55}, ${centerX + w + curveDir * 0.3} ${h * 0.25}, ${centerX + w} 0
                            Z`}
                        fill={rootFill}
                        stroke={rootStroke}
                        strokeWidth={1}
                    />
                );
            }
        } else {
            // Molars
            if (isUpper) {
                // Upper molars: trunk + 3 roots (palatal behind, 2 buccal in front)
                const trunkW = 15;
                const splitY = 30;
                const mbTipH = 74;
                const dbTipH = 68;
                const pTipH = 71;

                // Palatal root (behind/shadow, center, longest - curves slightly)
                rootElements.push(
                    <path
                        key="root-palatal"
                        d={`M ${centerX + 3} ${splitY * 0.7}
                            C ${centerX + 4} ${splitY + 10}, ${centerX + 3} ${pTipH * 0.6}, ${centerX + 1} ${pTipH * 0.92}
                            Q ${centerX} ${pTipH} ${centerX - 1} ${pTipH * 0.92}
                            C ${centerX - 3} ${pTipH * 0.6}, ${centerX - 4} ${splitY + 10}, ${centerX - 3} ${splitY * 0.7}
                            Z`}
                        fill={rootFillShadow}
                        stroke={rootStrokeShadow}
                        strokeWidth={1}
                    />
                );

                // Trunk (smooth curved base)
                rootElements.push(
                    <path
                        key="trunk"
                        d={`M ${centerX - trunkW} 0
                            L ${centerX - trunkW} ${splitY}
                            C ${centerX - trunkW * 0.6} ${splitY + 3}, ${centerX + trunkW * 0.6} ${splitY + 3}, ${centerX + trunkW} ${splitY}
                            L ${centerX + trunkW} 0
                            Z`}
                        fill={rootFill}
                        stroke={rootStroke}
                        strokeWidth={1}
                    />
                );

                // Mesiobuccal root (front left, curves mesially)
                rootElements.push(
                    <path
                        key="root-mb"
                        d={`M ${centerX - trunkW} ${splitY}
                            C ${centerX - trunkW - 1} ${splitY + 10}, ${centerX - trunkW - 1} ${mbTipH * 0.65}, ${centerX - trunkW + 4} ${mbTipH * 0.92}
                            Q ${centerX - trunkW + 5.5} ${mbTipH} ${centerX - trunkW + 7} ${mbTipH * 0.92}
                            C ${centerX - 5} ${mbTipH * 0.65}, ${centerX - 4} ${splitY + 6}, ${centerX - 3} ${splitY + 2}
                            C ${centerX - trunkW * 0.4} ${splitY + 3}, ${centerX - trunkW * 0.7} ${splitY + 1}, ${centerX - trunkW} ${splitY}
                            Z`}
                        fill={rootFill}
                        stroke={rootStroke}
                        strokeWidth={1}
                    />
                );

                // Distobuccal root (front right, shorter, curves distally)
                rootElements.push(
                    <path
                        key="root-db"
                        d={`M ${centerX + 3} ${splitY + 2}
                            C ${centerX + 4} ${splitY + 6}, ${centerX + 5} ${dbTipH * 0.65}, ${centerX + trunkW - 7} ${dbTipH * 0.92}
                            Q ${centerX + trunkW - 5.5} ${dbTipH} ${centerX + trunkW - 4} ${dbTipH * 0.92}
                            C ${centerX + trunkW + 1} ${dbTipH * 0.65}, ${centerX + trunkW + 1} ${splitY + 10}, ${centerX + trunkW} ${splitY}
                            C ${centerX + trunkW * 0.7} ${splitY + 1}, ${centerX + trunkW * 0.4} ${splitY + 3}, ${centerX + 3} ${splitY + 2}
                            Z`}
                        fill={rootFill}
                        stroke={rootStroke}
                        strokeWidth={1}
                    />
                );
            } else {
                // Lower molars: trunk + 2 roots (mesial and distal, slight overlap)
                const trunkW = 15;
                const splitY = 35;
                const mesialTipH = 76;
                const distalTipH = 71;

                // Distal root (behind/shadow, curves distally)
                rootElements.push(
                    <path
                        key="root-distal"
                        d={`M ${centerX + 3} ${splitY * 0.8}
                            C ${centerX + 4} ${splitY + 8}, ${centerX + 6} ${distalTipH * 0.6}, ${centerX + trunkW - 4} ${distalTipH * 0.92}
                            Q ${centerX + trunkW - 2.5} ${distalTipH} ${centerX + trunkW - 1} ${distalTipH * 0.92}
                            C ${centerX + trunkW + 1} ${distalTipH * 0.6}, ${centerX + trunkW + 1} ${splitY + 5}, ${centerX + trunkW} ${splitY * 0.8}
                            Z`}
                        fill={rootFillShadow}
                        stroke={rootStrokeShadow}
                        strokeWidth={1}
                    />
                );

                // Trunk + mesial root (front, curves mesially)
                rootElements.push(
                    <path
                        key="root-mesial"
                        d={`M ${centerX - trunkW} 0
                            L ${centerX - trunkW} ${splitY}
                            C ${centerX - trunkW - 1} ${splitY + 10}, ${centerX - trunkW - 1} ${mesialTipH * 0.6}, ${centerX - trunkW + 3} ${mesialTipH * 0.92}
                            Q ${centerX - trunkW + 4.5} ${mesialTipH} ${centerX - trunkW + 6} ${mesialTipH * 0.92}
                            C ${centerX - 2} ${mesialTipH * 0.6}, ${centerX - 1} ${splitY + 5}, ${centerX} ${splitY * 0.7}
                            L ${centerX} 0
                            Z
                            M ${centerX} 0
                            L ${centerX + trunkW} 0
                            L ${centerX + trunkW} ${splitY * 0.8}
                            C ${centerX + trunkW * 0.5} ${splitY * 0.85}, ${centerX + trunkW * 0.2} ${splitY * 0.8}, ${centerX} ${splitY * 0.7}
                            Z`}
                        fill={rootFill}
                        stroke={rootStroke}
                        strokeWidth={1}
                    />
                );
            }
        }

        return (
            <g transform={transform} opacity={0.65}>
                {rootElements}
            </g>
        );
    };

    /**
     * Render table rows for a tooth
     */
    const renderTableRows = (tooth, locations, isMissing, isImplant, toothNum, rowHeight, yOffset) => {
        return (
            <g transform={`translate(0, ${yOffset})`}>
                {/* Row 1: Tooth Number */}
                <rect x={0} y={0} width={toothWidth} height={rowHeight} fill={isDark ? "#2a2a2a" : "#f5f5f5"} stroke={isDark ? "#666" : "#000"} strokeWidth={0.5} />
                <text x={toothWidth / 2} y={rowHeight / 2} textAnchor="middle" dominantBaseline="middle" fontSize={10} fontWeight="bold" fill={isDark ? "#aaa" : "#666"}>
                    {toothNum}
                </text>

                {/* Row 2: Mobility */}
                <rect x={0} y={rowHeight} width={toothWidth} height={rowHeight} fill={isDark ? "#1e1e1e" : "#fff"} stroke={isDark ? "#666" : "#000"} strokeWidth={0.5} />
                {!isMissing && tooth.mobility && (
                    <text x={toothWidth / 2} y={rowHeight * 1.5} textAnchor="middle" dominantBaseline="middle" fontSize={10} fontWeight="bold" fill={isDark ? "#aaa" : "#666"}>
                        {tooth.mobility}
                    </text>
                )}

                {/* Row 3: Implant */}
                <rect x={0} y={rowHeight * 2} width={toothWidth} height={rowHeight} fill={isDark ? "#1e1e1e" : "#fff"} stroke={isDark ? "#666" : "#000"} strokeWidth={0.5} />
                {isImplant && (
                    <text x={toothWidth / 2} y={rowHeight * 2.5} textAnchor="middle" dominantBaseline="middle" fontSize={10} fill={isDark ? "#aaa" : "#666"}>
                        IMP
                    </text>
                )}

                {/* Row 4: Furcation */}
                <rect x={0} y={rowHeight * 3} width={toothWidth} height={rowHeight} fill={isDark ? "#1e1e1e" : "#fff"} stroke={isDark ? "#666" : "#000"} strokeWidth={0.5} />
                {!isMissing &&
                    locations.map((loc, idx) => {
                        const locX = (toothWidth / 3) * idx + toothWidth / 6;
                        const furcation = tooth[loc]?.furcation || false;
                        if (!furcation) return null;

                        return (
                            <text
                                key={`furcation-${loc}`}
                                x={locX}
                                y={rowHeight * 3.5}
                                textAnchor="middle"
                                dominantBaseline="middle"
                                fontSize={10}
                                fontWeight="bold"
                                fill={isDark ? "#aaa" : "#666"}>
                                {furcation}
                            </text>
                        );
                    })}

                {/* Row 5: Bleeding on Probing */}
                <rect x={0} y={rowHeight * 4} width={toothWidth} height={rowHeight} fill={isDark ? "#1e1e1e" : "#fff"} stroke={isDark ? "#666" : "#000"} strokeWidth={0.5} />
                {!isMissing &&
                    locations.map((loc, idx) => {
                        const locX = (toothWidth / 3) * idx + toothWidth / 6;
                        const bleeding = tooth[loc]?.bleeding || false;
                        if (!bleeding) return null;

                        return <circle key={`bleeding-${loc}`} cx={locX} cy={rowHeight * 4.5} r={3} fill="#d32f2f" />;
                    })}

                {/* Row 6: Plaque */}
                <rect x={0} y={rowHeight * 5} width={toothWidth} height={rowHeight} fill={isDark ? "#1e1e1e" : "#fff"} stroke={isDark ? "#666" : "#000"} strokeWidth={0.5} />
                {!isMissing &&
                    locations.map((loc, idx) => {
                        const locX = (toothWidth / 3) * idx + toothWidth / 6;
                        const plaque = tooth[loc]?.plaque || false;
                        if (!plaque) return null;

                        return <rect key={`plaque-${loc}`} x={locX - 3} y={rowHeight * 5.5 - 3} width={6} height={6} fill="#cf78df" />;
                    })}

                {/* Row 7: Suppuration */}
                <rect x={0} y={rowHeight * 6} width={toothWidth} height={rowHeight} fill={isDark ? "#1e1e1e" : "#fff"} stroke={isDark ? "#666" : "#000"} strokeWidth={0.5} />
                {!isMissing &&
                    locations.map((loc, idx) => {
                        const locX = (toothWidth / 3) * idx + toothWidth / 6;
                        const sup = tooth[loc]?.sup || false;
                        if (!sup) return null;

                        return <rect key={`sup-${loc}`} x={locX - 3} y={rowHeight * 6.5 - 3} width={6} height={6} fill="#ff9800" />;
                    })}

                {/* Row 8: Gingival Margin (recession) - 3 columns */}
                {/* Negative values = gingival overgrowth (shown in purple) */}
                <rect x={0} y={rowHeight * 7} width={toothWidth} height={rowHeight} fill={isDark ? "#1e1e1e" : "#fff"} stroke={isDark ? "#666" : "#000"} strokeWidth={0.5} />
                {!isMissing &&
                    locations.map((loc, idx) => {
                        const locX = (toothWidth / 3) * idx + toothWidth / 6;
                        const recession = tooth[loc]?.recession || 0;
                        if (recession === 0) return null;

                        // Purple for negative (overgrowth), red for positive (recession)
                        const textColor = recession < 0 ? "#4629ee" : "#1976d2";

                        return (
                            <text
                                key={`recession-${loc}`}
                                x={locX}
                                y={rowHeight * 7.5}
                                textAnchor="middle"
                                dominantBaseline="middle"
                                fontSize={9}
                                fill={textColor}
                                fontWeight="bold">
                                {recession}
                            </text>
                        );
                    })}

                {/* Row 9: Probing Depth - 3 columns */}
                {/* Red for depths >= 4mm, otherwise black */}
                <rect x={0} y={rowHeight * 8} width={toothWidth} height={rowHeight} fill={isDark ? "#1e1e1e" : "#fff"} stroke={isDark ? "#666" : "#000"} strokeWidth={0.5} />
                {!isMissing &&
                    locations.map((loc, idx) => {
                        const locX = (toothWidth / 3) * idx + toothWidth / 6;
                        const depth = tooth[loc]?.probing_depth || 0;
                        if (depth === 0) return null;

                        // Red for risk depths (>= 4mm), black otherwise
                        const textColor = depth >= 4 ? "#d32f2f" : isDark ? "#aaa" : "#000";

                        return (
                            <text key={`depth-${loc}`} x={locX} y={rowHeight * 8.5} textAnchor="middle" dominantBaseline="middle" fontSize={9} fill={textColor} fontWeight="bold">
                                {depth}
                            </text>
                        );
                    })}
            </g>
        );
    };

    /**
     * Render grid for a tooth
     */
    const renderGrid = (x, view, isTopView) => {
        return (
            <>
                <rect x={0} y={0} width={toothWidth} height={gridHeight} fill={isDark ? "#1e1e1e" : "#fff"} stroke={isDark ? "#666" : "#000"} strokeWidth={1} />
                {/* Horizontal gridlines every 1mm (15 internal lines, excluding top/bottom borders) */}
                {[...Array(15)].map((_, i) => {
                    const mm = i + 1;
                    // In inverted (buccal) view, the mm value from 0 is (gridMm - mm)
                    const mmFrom0 = isTopView ? gridMm - mm : mm;
                    const isBold = mmFrom0 === 5 || mmFrom0 === 10;
                    return (
                        <line
                            key={`grid-${view}-${mm}`}
                            x1={0}
                            y1={mm * pxPerMm}
                            x2={toothWidth}
                            y2={mm * pxPerMm}
                            stroke={isBold ? (isDark ? "#666" : "#999") : isDark ? "#444" : "#ddd"}
                            strokeWidth={isBold ? 0.8 : 0.5}
                        />
                    );
                })}
                {/* Grid scale numbers on first tooth - different for top vs bottom view */}
                {x === 0 &&
                    [0, 5, 10, 15].map((mm) => {
                        // Top view (buccal): 0 at bottom, mm increases upward
                        // Bottom view (palatal/lingual): 0 at top, mm increases downward
                        const y = isTopView ? (gridMm - mm) * pxPerMm : mm * pxPerMm;
                        return (
                            <text
                                key={`scale-${view}-${mm}`}
                                x={tableScaleOffset}
                                y={y}
                                textAnchor="end"
                                fontSize={9}
                                fill={isDark ? "#aaa" : "#333"}
                                fontWeight="bold"
                                dominantBaseline="middle">
                                {mm}
                            </text>
                        );
                    })}
            </>
        );
    };

    /**
     * Render a single tooth column with all measurements
     * @param {number} permanentToothNum - The permanent tooth position (for layout)
     * @param {number} effectiveToothNum - The actual tooth to display (may be deciduous)
     */
    const renderElements = (permanentToothNum, effectiveToothNum, x, y, view, isFirstTooth = false, isBuccalView = false) => {
        const tooth = teethData[effectiveToothNum];
        if (!tooth) return null;
        const toothNum = effectiveToothNum;

        const isMissing = tooth.missing;
        const isImplant = tooth.implant;

        // For upper jaw buccal view: table at top, grid in middle, tooth crown at bottom (upside down)
        // For upper jaw palatal view: tooth crown at top (normal), grid in middle, table at bottom
        const isUpperBuccal = arch === "upper" && isBuccalView;
        const isUpperPalatal = arch === "upper" && !isBuccalView;
        const isLowerBuccal = arch === "lower" && isBuccalView;
        const isLowerLingual = arch === "lower" && !isBuccalView;

        // Determine which locations to show based on view
        // For Q2/Q3 (left side), mesial is toward the midline (left on chart), so reverse the order
        const quadrant = Math.floor(effectiveToothNum / 10);
        const isLeftSide = quadrant === 2 || quadrant === 3 || quadrant === 6 || quadrant === 7;

        let locations = [];
        if (isUpperBuccal) locations = isLeftSide ? ["mb", "b", "db"] : ["db", "b", "mb"];
        else if (isUpperPalatal) locations = isLeftSide ? ["mp", "p", "dp"] : ["dp", "p", "mp"];
        else if (isLowerBuccal) locations = isLeftSide ? ["mb", "b", "db"] : ["db", "b", "mb"];
        else if (isLowerLingual) locations = isLeftSide ? ["ml", "l", "dl"] : ["dl", "l", "ml"];

        return (
            <g key={`${permanentToothNum}-${view}`} transform={`translate(${x}, ${y})`}>
                {/* Row labels on first tooth */}
                {isFirstTooth &&
                    tableLabels.map((label, idx) => {
                        const labelY = isUpperPalatal || isLowerLingual ? toothHeight + gridHeight + tableGridGap + tableRowHeight * (idx + 0.5) : tableRowHeight * (idx + 0.5);

                        return (
                            <text key={`label-${idx}`} x={tableLabelOffset} y={labelY} textAnchor="end" fontSize={9} fill={isDark ? "#aaa" : "#666"} dominantBaseline="middle">
                                {label}
                            </text>
                        );
                    })}

                {/* Render based on view type */}
                {isUpperBuccal ? (
                    <>
                        {/* Upper Buccal (TOP VIEW): Table -> Gap -> Grid(10-0) with roots behind -> Crown(upside down) at grid bottom */}
                        {/* Table at top */}
                        {renderTableRows(tooth, locations, isMissing, isImplant, toothNum, tableRowHeight, 0)}

                        {/* Grid with roots behind (after gap) */}
                        <g transform={`translate(0, ${tableHeight + tableGridGap})`}>
                            {/* Grid in front */}
                            {renderGrid(x, view, true)}

                            {/* Roots behind grid */}
                            {renderToothRoots(toothNum, toothWidth, gridHeight, isMissing, isImplant, true)}
                        </g>

                        {/* Crown at bottom of grid (upside down, touching grid) */}
                        {renderToothCrown(toothNum, toothWidth, toothHeight, isMissing, isImplant, true)}
                    </>
                ) : isUpperPalatal ? (
                    <>
                        {/* Table at bottom (with gap) */}
                        {renderTableRows(tooth, locations, isMissing, isImplant, toothNum, tableRowHeight, toothHeight + gridHeight + tableGridGap)}

                        {/* Grid with roots behind (touching crown) */}
                        <g transform={`translate(0, ${toothHeight})`}>
                            {/* Grid in front */}
                            {renderGrid(x, view, false)}

                            {/* Roots behind grid */}
                            {renderToothRoots(toothNum, toothWidth, gridHeight, isMissing, isImplant, false)}
                        </g>

                        {/* Upper Palatal (BOTTOM VIEW): Crown(normal) -> Grid(0-10) with roots behind -> Gap -> Table */}
                        {/* Crown at top (normal orientation) */}
                        {renderToothCrown(toothNum, toothWidth, toothHeight, isMissing, isImplant, false)}
                    </>
                ) : (
                    <>
                        {/* Lower jaw: TOP VIEW (Lingual) or BOTTOM VIEW (Buccal) */}
                        {isLowerBuccal ? (
                            <>
                                {/* Lower Lingual (TOP VIEW): Table -> Gap -> Grid(10-0) with roots behind -> Crown(upside down) */}
                                {/* Table at top */}
                                {renderTableRows(tooth, locations, isMissing, isImplant, toothNum, tableRowHeight, 0)}

                                {/* Grid with roots behind (after gap) */}
                                <g transform={`translate(0, ${tableHeight + tableGridGap})`}>
                                    {/* Grid in front */}
                                    {renderGrid(x, view, true)}

                                    {/* Roots behind grid */}
                                    {renderToothRoots(toothNum, toothWidth, gridHeight, isMissing, isImplant, true)}
                                </g>

                                {/* Crown at bottom of grid (upside down) */}
                                {renderToothCrown(toothNum, toothWidth, toothHeight, isMissing, isImplant, true)}
                            </>
                        ) : (
                            <>
                                {/* Table at bottom (with gap) */}
                                {renderTableRows(tooth, locations, isMissing, isImplant, toothNum, tableRowHeight, toothHeight + gridHeight + tableGridGap)}

                                {/* Grid with roots behind (touching crown) */}
                                <g transform={`translate(0, ${toothHeight})`}>
                                    {/* Grid in front */}
                                    {renderGrid(x, view, false)}

                                    {/* Roots behind grid */}
                                    {renderToothRoots(toothNum, toothWidth, gridHeight, isMissing, isImplant, false)}
                                </g>

                                {/* Lower Buccal (BOTTOM VIEW): Crown -> Grid(0-10) with roots behind -> Gap -> Table */}
                                {/* Crown at top (normal orientation) */}
                                {renderToothCrown(toothNum, toothWidth, toothHeight, isMissing, isImplant, false)}
                            </>
                        )}
                    </>
                )}
            </g>
        );
    };

    /**
     * Render measurement lines (red for recession, blue for probing depth)
     * Lines connect the three measurement points across teeth, breaking at missing teeth and quadrant boundaries
     */
    const renderMeasurementLines = (y, view, rightTeethCount, gap, isBuccalView = false) => {
        const isUpper = arch === "upper";
        const isUpperBuccal = isUpper && isBuccalView;
        const isUpperPalatal = isUpper && !isBuccalView;
        const isLowerBuccal = !isUpper && isBuccalView;
        const isLowerLingual = !isUpper && !isBuccalView;

        // Collect paired line segments (each segment has red and blue points)
        // Segments break at missing teeth and quadrant boundaries
        const segments = [];
        let currentSegment = { red: [], blue: [] };

        const saveCurrentSegment = () => {
            if (currentSegment.red.length > 0 && currentSegment.blue.length > 0) {
                segments.push(currentSegment);
            }
            currentSegment = { red: [], blue: [] };
        };

        allTeeth.forEach((permanentToothNum, toothIdx) => {
            const effectiveToothNum = getEffectiveTooth(permanentToothNum);
            const tooth = teethData[effectiveToothNum];
            const isFirstOfLeftQuadrant = toothIdx === rightTeethCount;

            // Determine locations per tooth (quadrant-aware mesial/distal ordering)
            const quadrant = Math.floor(effectiveToothNum / 10);
            const isLeftSide = quadrant === 2 || quadrant === 3 || quadrant === 6 || quadrant === 7;

            let locations = [];
            if (isUpperBuccal) locations = isLeftSide ? ["mb", "b", "db"] : ["db", "b", "mb"];
            else if (isUpperPalatal) locations = isLeftSide ? ["mp", "p", "dp"] : ["dp", "p", "mp"];
            else if (isLowerBuccal) locations = isLeftSide ? ["mb", "b", "db"] : ["db", "b", "mb"];
            else if (isLowerLingual) locations = isLeftSide ? ["ml", "l", "dl"] : ["dl", "l", "ml"];

            // Break segments at quadrant boundary
            if (isFirstOfLeftQuadrant) {
                saveCurrentSegment();
            }

            // Break segments at missing teeth
            if (!tooth || tooth.missing) {
                saveCurrentSegment();
                return;
            }

            locations.forEach((loc, locIdx) => {
                const isInvertedGrid = isUpperBuccal || isLowerBuccal;

                // Add gap after right quadrant
                const xOffset = toothIdx >= rightTeethCount ? midlineGap : 0;
                const x = toothIdx * toothWidth + (toothWidth / 3) * locIdx + toothWidth / 6 + xOffset;
                const recession = tooth[loc]?.recession || 0;
                const depth = tooth[loc]?.probing_depth || 0;

                // Clamp recession to grid range (negative = gingival overgrowth)
                const clampedRecession = Math.max(-gridMm, Math.min(recession, gridMm));

                // Clinical attachment level = recession + probing depth
                // This represents the total pocket depth from the baseline (CEJ)
                // For negative recession (overgrowth), the attachment level is still recession + depth
                const attachmentLevel = Math.max(-gridMm, Math.min(clampedRecession + depth, gridMm));

                // Red line: gingival margin (recession)
                // Negative recession (overgrowth) goes above baseline, positive goes below
                let redY = toothHeight + clampedRecession * pxPerMm;

                // if dealing with grid 10-0, the number is inverted
                if (isInvertedGrid) {
                    redY = tableHeight + tableGridGap + (gridMm - clampedRecession) * pxPerMm;
                }

                currentSegment.red.push({ x, y: redY });

                // Blue line: probing depth (recession + probing_depth combined)
                let blueY = toothHeight + attachmentLevel * pxPerMm;

                // if dealing with grid 10-0, the number is inverted
                if (isInvertedGrid) {
                    blueY = tableHeight + tableGridGap + (gridMm - attachmentLevel) * pxPerMm;
                }

                currentSegment.blue.push({ x, y: blueY });
            });
        });

        // Don't forget the last segment
        saveCurrentSegment();

        // Create path string from a segment
        const createPath = (points) => {
            if (points.length === 0) return "";
            return points.map((p, i) => `${i === 0 ? "M" : "L"} ${p.x} ${p.y}`).join(" ");
        };

        // Create filled area path between red and blue lines
        const createFilledPath = (redPoints, bluePoints) => {
            if (redPoints.length === 0 || bluePoints.length === 0) return "";
            // Go along red line, then back along blue line (reversed) to close the shape
            const redPath = redPoints.map((p, i) => `${i === 0 ? "M" : "L"} ${p.x} ${p.y}`).join(" ");
            const bluePath = [...bluePoints]
                .reverse()
                .map((p) => `L ${p.x} ${p.y}`)
                .join(" ");
            return `${redPath} ${bluePath} Z`;
        };

        return (
            <g transform={`scale(1, 1) translate(0, 0)`} opacity={1}>
                {/* Filled area between lines (pocket depth visualization) */}
                {segments.map((segment, idx) => {
                    const fillPath = createFilledPath(segment.red, segment.blue);
                    return fillPath ? <path key={`fill-${idx}`} d={fillPath} fill="#bbdefb" fillOpacity={0.5} stroke="none" /> : null;
                })}

                {/* Red lines (recession) */}
                {segments.map((segment, idx) => {
                    const path = createPath(segment.red);
                    return path ? <path key={`red-${idx}`} d={path} fill="none" stroke="#d32f2f" strokeWidth={2} strokeOpacity={0.8} /> : null;
                })}

                {/* Blue lines (probing depth) */}
                {segments.map((segment, idx) => {
                    const path = createPath(segment.blue);
                    return path ? <path key={`blue-${idx}`} d={path} fill="none" stroke="#1976d2" strokeWidth={2} strokeOpacity={0.8} /> : null;
                })}
            </g>
        );
    };

    return (
        <Paper
            id={`chart-${arch}`}
            elevation={0}
            sx={{
                p: 2,
                width: "100%",
                overflow: "auto",
                maxWidth: "100%",
                margin: "0 auto",
                display: "flex",
                justifyContent: "center",
                "& svg": {
                    maxWidth: "100%",
                    height: "auto"
                }
            }}>
            <svg width={chartWidth} height={chartHeight} viewBox={`0 0 ${chartWidth} ${chartHeight}`} preserveAspectRatio="xMidYMid meet" style={{ fontFamily: "inherit" }}>
                {/* Header */}
                <rect x={0} y={0} width={chartWidth} height={headerHeight} fill={isDark ? "#2a2a2a" : "#f5f5f5"} />
                <text x={chartWidth / 2} y={30} textAnchor="middle" fontSize={24} fontWeight="bold" fill={isDark ? "#fff" : "#000"}>
                    {arch === "upper" ? "Upper Arch" : "Lower Arch"} Periodontal Chart
                </text>

                {patientInfo.name && (
                    <text x={chartWidth / 2} y={55} textAnchor="middle" fontSize={14} fill={isDark ? "#ccc" : "#000"}>
                        Patient: {patientInfo.name} | Date: {patientInfo.date || new Date().toLocaleDateString()}
                    </text>
                )}

                {/* Top view (Buccal for both) */}
                <g transform={`translate(${upperTableX}, ${upperTableY})`}>
                    <text
                        x={upperGridLegendX}
                        y={upperGridLegendY}
                        fontSize={16}
                        fontWeight="bold"
                        fill={isDark ? "#fff" : "#000"}
                        transform={`rotate(-90, ${upperGridLegendX}, ${upperGridLegendY})`}>
                        {topView}
                    </text>

                    {/* Render all teeth */}
                    {allTeeth.map((permanentToothNum, idx) => {
                        // Add gap after right quadrant (first 8 teeth)
                        const xOffset = idx >= rightTeeth.length ? midlineGap : 0;
                        const effectiveToothNum = getEffectiveTooth(permanentToothNum);
                        return renderElements(permanentToothNum, effectiveToothNum, idx * toothWidth + xOffset, 0, topView, idx === 0, true);
                    })}

                    {/* Midline separator */}
                    <line
                        x1={rightTeeth.length * toothWidth + midlineGap / 2}
                        y1={0}
                        x2={rightTeeth.length * toothWidth + midlineGap / 2}
                        y2={midlineY2}
                        stroke={isDark ? "#888" : "#666"}
                        strokeWidth={2}
                        strokeDasharray="5,5"
                    />

                    {/* Measurement lines overlay */}
                    {renderMeasurementLines(0, topView, rightTeeth.length, tableGridGap, true)}
                </g>

                {/* Bottom view (Palatal/Lingual) */}
                <g transform={`translate(${lowerTableX}, ${lowerTableY})`}>
                    <text
                        x={lowerGridLegendX}
                        y={lowerGridLegendY}
                        fontSize={16}
                        fontWeight="bold"
                        fill={isDark ? "#fff" : "#000"}
                        transform={`rotate(-90, ${lowerGridLegendX}, ${lowerGridLegendY})`}>
                        {bottomView}
                    </text>

                    {/* Render all teeth */}
                    {allTeeth.map((permanentToothNum, idx) => {
                        // Add gap after right quadrant (first 8 teeth)
                        const xOffset = idx >= rightTeeth.length ? midlineGap : 0;
                        const effectiveToothNum = getEffectiveTooth(permanentToothNum);
                        return renderElements(permanentToothNum, effectiveToothNum, idx * toothWidth + xOffset, 0, bottomView, idx === 0, false);
                    })}

                    {/* Midline separator */}
                    <line
                        x1={rightTeeth.length * toothWidth + midlineGap / 2}
                        y1={45}
                        x2={rightTeeth.length * toothWidth + midlineGap / 2}
                        y2={midlineY2 + 45}
                        stroke={isDark ? "#888" : "#666"}
                        strokeWidth={2}
                        strokeDasharray="5,5"
                    />

                    {/* Measurement lines overlay */}
                    {renderMeasurementLines(0, bottomView, rightTeeth.length, tableGridGap, false)}
                </g>
            </svg>
        </Paper>
    );
}
